import Cookie from './cookie';
import {
  BROKER_API_CACHE_CONFIG,
  getBrokerApiCacheTtlMs,
  getEnabledBrokerApiEventTypes,
  isBrokerApiCacheEnabled,
  isSessionScopedBrokerApi,
} from './apiCacheConfig';

const DB_NAME = 'kn_api_cache';
const STORE_NAME = 'api_responses';
const DB_VERSION = 1;

export {
  CACHED_EVENT_TYPES,
  FIXED_TTL_EVENT_TYPES,
  SESSION_CACHE_EVENT_TYPES,
} from './apiCacheConfig';

/** Engine iframe rotates kx token per shipment; broker-level cache must not depend on token value. */
function isEngineIframeMode() {
  return window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN === 'kxEngineBrokerToken';
}

function getSessionCacheScope() {
  if (isEngineIframeMode()) {
    return 'engine-iframe';
  }
  return Cookie.get(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN) || '';
}

function stableEventMessageKey(eventMessage) {
  if (eventMessage === null || eventMessage === undefined || eventMessage === '') return '';
  if (typeof eventMessage !== 'object') return String(eventMessage);
  if (Array.isArray(eventMessage)) return JSON.stringify(eventMessage);
  const sorted = Object.keys(eventMessage).sort().reduce((acc, key) => {
    acc[key] = eventMessage[key];
    return acc;
  }, {});
  return JSON.stringify(sorted);
}

export function isEmptyValue(value) {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return true;
  return false;
}

/** Returns true when the API body is successful and has a non-empty eventMessage. */
export function isCacheableResponse(data, eventType) {
  if (!data || data.msg === 'Failed') return false;
  const eventMessage = data.eventMessage;
  const code = eventMessage?.code;
  if (code !== undefined && code !== null && Number(code) >= 400) return false;

  if (eventType === 'GET_CARRIER_CODE') {
    return Array.isArray(eventMessage?.carrierCodes) && eventMessage.carrierCodes.length > 0;
  }
  if (eventType === 'GET_CURRENCY_CODE') {
    return Array.isArray(eventMessage?.currencyCodes) && eventMessage.currencyCodes.length > 0;
  }
  if (eventType === 'GET_DOCUMENT_TYPES') {
    return Array.isArray(eventMessage?.docTypes) && eventMessage.docTypes.length > 0;
  }
  if (eventType === 'GET_VOLUNTARY_PGA') {
    return Array.isArray(eventMessage?.pgaMetadataList) && eventMessage.pgaMetadataList.length > 0;
  }

  return !isEmptyValue(eventMessage);
}

export function createCachedAxiosResponse(data, config = {}) {
  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    fromCache: true,
  };
}

export function createCachedFetchResponse(data, status = 200) {
  return {
    ok: status >= 200 && status < 300,
    status,
    json: () => Promise.resolve(data),
    fromCache: true,
  };
}

class ApiIDBCache {
  constructor() {
    this._dbPromise = null;
  }

  isCachedEventType(eventType) {
    return isBrokerApiCacheEnabled(eventType);
  }

  getTtlMs(eventType) {
    return getBrokerApiCacheTtlMs(eventType);
  }

  buildCacheKey(eventType, eventMessage = {}) {
    const messageKey = stableEventMessageKey(eventMessage);
    if (isSessionScopedBrokerApi(eventType)) {
      const scope = getSessionCacheScope();
      return `${eventType}__${scope}__${messageKey}`;
    }
    return `${eventType}__${messageKey}`;
  }

  openDB() {
    if (this._dbPromise) return this._dbPromise;
    this._dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
        }
      };
      req.onsuccess = (e) => resolve(e.target.result);
      req.onerror = () => {
        console.error('[apiIDBCache] Failed to open IndexedDB:', req.error);
        this._dbPromise = null;
        reject(req.error);
      };
    });
    return this._dbPromise;
  }

  async get(cacheKey) {
    try {
      const db = await this.openDB();
      return await new Promise((resolve) => {
        const req = db.transaction(STORE_NAME, 'readonly')
          .objectStore(STORE_NAME)
          .get(cacheKey);
        req.onsuccess = () => {
          const record = req.result;
          if (!record || Date.now() > record.expiresAt) return resolve(null);
          if (!isBrokerApiCacheEnabled(record.eventType)) return resolve(null);
          resolve(record.data);
        };
        req.onerror = () => {
          console.error('[apiIDBCache] Read error for key:', cacheKey, req.error);
          resolve(null);
        };
      });
    } catch (e) {
      console.error('[apiIDBCache] get failed:', e);
      return null;
    }
  }

  async set(cacheKey, data, eventType) {
    if (!isBrokerApiCacheEnabled(eventType)) return;
    if (!isCacheableResponse(data, eventType)) return;
    const ttlMs = this.getTtlMs(eventType);
    if (!ttlMs) return;

    try {
      const db = await this.openDB();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put({
          cacheKey,
          data,
          eventType,
          ttlType: isSessionScopedBrokerApi(eventType) ? 'session' : 'fixed',
          expiresAt: Date.now() + ttlMs,
        });
        tx.oncomplete = resolve;
        tx.onerror = () => {
          console.error('[apiIDBCache] Write error for key:', cacheKey, tx.error);
          reject(tx.error);
        };
      });
    } catch (e) {
      console.error('[apiIDBCache] set failed:', e);
    }
  }

  async purgeDisabledApiEntries() {
    const disabledEventTypes = new Set(
      Object.entries(BROKER_API_CACHE_CONFIG)
        .filter(([, cfg]) => !cfg.enabled)
        .map(([eventType]) => eventType)
    );
    if (!disabledEventTypes.size) return;

    try {
      const db = await this.openDB();
      await new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.openCursor();
        req.onsuccess = (e) => {
          const cursor = e.target.result;
          if (!cursor) return;
          if (disabledEventTypes.has(cursor.value.eventType)) cursor.delete();
          cursor.continue();
        };
        req.onerror = () => {
          console.error('[apiIDBCache] purgeDisabledApiEntries cursor error:', req.error);
        };
        tx.oncomplete = resolve;
        tx.onerror = resolve;
      });
    } catch (e) {
      console.error('[apiIDBCache] purgeDisabledApiEntries failed:', e);
    }
  }

  async pruneExpired() {
    try {
      const db = await this.openDB();
      await new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.openCursor();
        req.onsuccess = (e) => {
          const cursor = e.target.result;
          if (!cursor) return;
          if (Date.now() > cursor.value.expiresAt) cursor.delete();
          cursor.continue();
        };
        req.onerror = () => {
          console.error('[apiIDBCache] Prune cursor error:', req.error);
        };
        tx.oncomplete = resolve;
        tx.onerror = resolve;
      });
    } catch (e) {
      console.error('[apiIDBCache] pruneExpired failed:', e);
    }
  }

  async clearAll() {
    try {
      const db = await this.openDB();
      await new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).clear();
        tx.oncomplete = resolve;
        tx.onerror = resolve;
      });
    } catch (e) {
      console.error('[apiIDBCache] clearAll failed:', e);
    }
  }

  async clearSessionEntries() {
    const sessionEventTypes = new Set(
      getEnabledBrokerApiEventTypes().filter(isSessionScopedBrokerApi)
    );

    try {
      const db = await this.openDB();
      await new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.openCursor();
        req.onsuccess = (e) => {
          const cursor = e.target.result;
          if (!cursor) return;
          const { eventType, ttlType } = cursor.value;
          if (ttlType === 'session' || sessionEventTypes.has(eventType)) {
            cursor.delete();
          }
          cursor.continue();
        };
        req.onerror = () => {
          console.error('[apiIDBCache] clearSessionEntries cursor error:', req.error);
        };
        tx.oncomplete = resolve;
        tx.onerror = resolve;
      });
    } catch (e) {
      console.error('[apiIDBCache] clearSessionEntries failed:', e);
    }
  }

  async wrapAxiosRequest(config, axiosRequest) {
    const eventType = config.data?.eventType;
    if (!this.isCachedEventType(eventType)) {
      return axiosRequest(config);
    }

    const needsToken = isSessionScopedBrokerApi(eventType);
    const hasAuth = Boolean(Cookie.get(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN));
    if (needsToken && !hasAuth && !isEngineIframeMode()) {
      return axiosRequest(config);
    }

    const cacheKey = this.buildCacheKey(eventType, config.data.eventMessage);
    const cached = await this.get(cacheKey);
    if (cached) return createCachedAxiosResponse(cached, config);

    const response = await axiosRequest(config);
    if (isCacheableResponse(response.data, eventType)) {
      await this.set(cacheKey, response.data, eventType);
    }
    return response;
  }
}

export const apiIDBCache = new ApiIDBCache();
